import { useState, useMemo , useEffect} from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import useLocalization from "../../../../hooks/localization";
import { updateLanguage } from "../../../../redux/actions/orgActions";
import { CHAT_WITH_BOT } from "../../../../constants/chat";
import { ALL_LANGUAGES } from "../../../../constants/languages";

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.sm};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.veryDarkGrey};
`;

const MenuSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const LanguageText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${({ theme }) => theme.reg};
  color: ${({ selected, theme }) => 
    selected ? theme.primaryColor : theme.fontColor};
  font-weight: 400;
  transition: color 0.15s ease;
`;

const LanguageContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LanguageItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: transparent;

  &:hover {
    ${LanguageText} {
      color: ${({ theme }) => theme.primaryColor};
      font-weight: 600;
    }
  }
`;

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primaryColor};
  opacity: ${({ selected }) => (selected ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

const Divider = styled.hr`
  margin: 0.75rem 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.veryLightGrey};
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  font-size: ${({ theme }) => theme.sm};
  color: ${({ theme }) => theme.fontColor};
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.veryLightGrey};
  border-radius: 6px;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ theme }) => theme.primaryColor};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.veryDarkGrey};
    opacity: 1;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.darkGrey};
  font-size: 1.25rem;
  pointer-events: none;
`;

const ScrollableLanguageList = styled(LanguageList)`
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const NoResults = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.darkGrey};
  font-size: ${({ theme }) => theme.sm};
`;


function LanguagePreferencesSection({ showNotification }) {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");

  const customizations = useSelector((state) => state?.org?.customizations);
  const localizationConfig = customizations?.localizationConfig;
  const chatConfig = customizations?.chatConfig;
  const supportedLangs = localizationConfig?.supported
    ? { ...localizationConfig?.supported }
    : {};

  const openMenu = useSelector((state) => state?.chat?.openAccessibilityMenu);
  const tooltips = useSelector(
    (state) => state?.org?.customizations?.tooltips?.replyBar
  );
  const { localizeTooltips } = useLocalization();
  const localizedTooltips = localizeTooltips(tooltips);

  const chatWith = useSelector((state) => state?.chat?.chatWith);
  const isBotTyping = useSelector((state) => state?.chat?.isBotTyping);
  const lang = useSelector((state) => state?.org?.lang);

  const disableMessageSubmission = chatWith === CHAT_WITH_BOT && isBotTyping;

  const setLang = (val) => {
    if (disableMessageSubmission) {
      return;
    }
    if (val !== lang) {
      dispatch(updateLanguage(val));
    }
  };

  const handleOnClick = (setter, value) => () => {
    setter(value);
  };

  useEffect(() => {
    if (lang) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const handleKeyDown = (setter, value) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setter(value);
    }
  };

  const { supportedLanguages, allLanguages, hasResults } = useMemo(() => {
    const isSearching = searchQuery.trim().length > 0;
    const query = searchQuery.toLowerCase();

    if (isSearching) {
      // When searching, combine and filter all languages
      const filtered = {};

      Object.entries(ALL_LANGUAGES).forEach(([code, name]) => {
        if (
          name.toLowerCase().includes(query) ||
          code.toLowerCase().includes(query)
        ) {
          filtered[code] = name;
        }
      });

      return {
        supportedLanguages: filtered,
        allLanguages: {},
        hasResults: Object.keys(filtered).length > 0,
      };
    }

    // When not searching, show supported languages first, then all languages
    return {
      supportedLanguages: { ...supportedLangs },
      allLanguages: { ...ALL_LANGUAGES },
      hasResults: true,
    };
  }, [searchQuery]);

  const renderLanguageItem = (code, name) => {
    const selected = lang === code;
    const displayName =
      supportedLangs[code] && localizedTooltips?.[supportedLangs[code]]
        ? localizedTooltips[supportedLangs[code]]
        : name;

    return (
      <LanguageItem
        key={code}
        role="radio"
        aria-checked={selected}
        selected={selected}
        tabIndex={0}
        onClick={handleOnClick(setLang, code)}
        aria-label={`Select ${name} language`}
        onKeyDown={handleKeyDown(setLang, code)}
        lang={code}
      >
        <LanguageContent>
          <LanguageText selected={selected}>{displayName}</LanguageText>

          {selected && (
            <CheckIconWrapper selected>
              <CheckIcon sx={{ fontSize: "1rem" }} />
            </CheckIconWrapper>
          )}
        </LanguageContent>
      </LanguageItem>
    );
  };

  return (
    <MenuSection>
      <SectionTitle id="lang-title">
        <TranslateIcon
          sx={{
            fontSize: "1rem",
            verticalAlign: "middle",
            marginRight: "0.5rem",
          }}
          aria-hidden="true"
        />
        Language Preferences
      </SectionTitle>

      <SearchContainer>
        <SearchIconWrapper>
          <SearchIcon sx={{ fontSize: "inherit" }} />
        </SearchIconWrapper>
        <SearchInput
          type="text"
          placeholder="Search languages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search for a language"
        />
      </SearchContainer>

      <ScrollableLanguageList role="radiogroup" aria-labelledby="lang-title">
        {hasResults ? (
          <>
            {Object.entries(supportedLanguages).map(([code, name]) =>
              renderLanguageItem(code, name)
            )}

            {Object.keys(supportedLanguages).length > 0 && 
             Object.keys(allLanguages).length > 0 && (
              <Divider />
            )}

            {Object.entries(allLanguages).map(([code, name]) =>
              renderLanguageItem(code, name)
            )}
          </>
        ) : (
          <NoResults>No languages found matching "{searchQuery}"</NoResults>
        )}
      </ScrollableLanguageList>
    </MenuSection>
  );
}

export default LanguagePreferencesSection;
